import * as React from "react";
import { Edit, SimpleForm, ReferenceInput, SelectInput, BooleanInput, TextInput, NumberInput, DateInput, useDataProvider, useNotify, Button } from 'react-admin';
import { FbDateRangeSelectInput } from "../components/FbDateField";
import { useProductionProvider } from "../ProductionContext";
import { Typography, Box, Divider, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

const SectionHeading = ({ title }) => (
    <Box mt={2} mb={1} style={{ width: '100%' }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Divider />
    </Box>
);

const HiatusesPanel = ({ resource, label = 'Hiatuses' }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [hiatuses, setHiatuses] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [form, setForm] = React.useState({ start_date: '', end_date: '', reason: '' });

    const load = () => {
        setLoading(true);
        dataProvider.getList(resource, { pagination: {}, sort: {}, filter: {} })
            .then(({ data }) => { setHiatuses(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => { load(); }, [resource]);

    const openAdd = () => {
        setEditing(null);
        setForm({ start_date: '', end_date: '', reason: '' });
        setDialogOpen(true);
    };

    const openEdit = (hiatus) => {
        setEditing(hiatus);
        setForm({ start_date: hiatus.start_date, end_date: hiatus.end_date, reason: hiatus.reason });
        setDialogOpen(true);
    };

    const handleSave = () => {
        const request = editing
            ? dataProvider.update(resource, { id: editing.id, data: form })
            : dataProvider.create(resource, { data: form });

        request.then(() => {
            notify(editing ? 'Hiatus updated' : 'Hiatus added', 'success');
            setDialogOpen(false);
            load();
        }).catch(err => {
            notify(err?.body?.reason?.[0] ?? err?.body?.start_date?.[0] ?? 'Error saving hiatus', 'error');
        });
    };

    const handleDelete = (id) => {
        dataProvider.delete(resource, { id })
            .then(() => { notify('Hiatus deleted', 'success'); load(); })
            .catch(() => notify('Error deleting hiatus', 'error'));
    };

    return (
        <Box mb={3} style={{ width: '100%' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">{label}</Typography>
                <Button label="Add Hiatus" onClick={openAdd}><AddIcon /></Button>
            </Box>
            {loading ? <Typography variant="body2">Loading...</Typography> : (
                hiatuses.length === 0
                    ? <Typography variant="body2" color="textSecondary">No hiatuses added.</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {hiatuses.map(h => (
                                    <TableRow key={h.id}>
                                        <TableCell>{h.start_date}</TableCell>
                                        <TableCell>{h.end_date}</TableCell>
                                        <TableCell>{h.reason}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => openEdit(h)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(h.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit Hiatus' : 'Add Hiatus'}</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <MuiTextField
                            label="Start Date"
                            type="date"
                            value={form.start_date}
                            onChange={e => setForm({ ...form, start_date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="End Date"
                            type="date"
                            value={form.end_date}
                            onChange={e => setForm({ ...form, end_date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    <Box mb={2}>
                        <MuiTextField
                            label="Reason"
                            value={form.reason}
                            onChange={e => setForm({ ...form, reason: e.target.value })}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setDialogOpen(false)} />
                    <Button label="Save" onClick={handleSave} />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export { HiatusesPanel };

export const ProductionSettingsScreen = props => {
    const { staticContext, ...rest } = props;
    const { production } = useProductionProvider();

    const timesheetPdfTemplates = [
        { id: 'standard', value: 'Standard' },
        { id: 'single_per_diem', value: 'Single Per Diem' },
        { id: 'shooting_per_diem', value: 'Shooting Per Diem' },
    ];

    const projectTypeChoices = [
        { id: 'high_end_tv', name: 'High End TV' },
        { id: 'major_motion_picture', name: 'Major Motion Picture' },
        { id: 'independent_motion_picture', name: 'Independent Motion Picture' },
        { id: 'unscripted', name: 'Unscripted' },
        { id: 'factual', name: 'Factual' },
        { id: 'continuing_drama', name: 'Continuing Drama' },
        { id: 'commercial', name: 'Commercial' },
        { id: 'animation', name: 'Animation' },
        { id: 'other', name: 'Other' },
    ];

    const budgetBandChoices = [
        { id: '1', name: 'Band 1' },
        { id: '2', name: 'Band 2' },
        { id: '3', name: 'Band 3' },
        { id: '4', name: 'Band 4' },
    ];

    const perDiemTypeChoices = [
        { id: 'meals', name: 'Meals' },
        { id: 'single', name: 'Single' },
        { id: 'shoot_non_shoot', name: 'Shoot / Non-Shoot' },
    ];

    const timezoneChoices = [
        { id: 'UTC+0', name: 'UTC+0 — London / Edinburgh' },
        { id: 'UTC+1', name: 'UTC+1 — BST (British Summer Time)' },
        { id: 'UTC-5', name: 'UTC-5 — New York / Toronto' },
        { id: 'UTC-6', name: 'UTC-6 — Chicago / Mexico City' },
        { id: 'UTC-7', name: 'UTC-7 — Denver / Phoenix' },
        { id: 'UTC-8', name: 'UTC-8 — Los Angeles / Vancouver' },
        { id: 'UTC+1', name: 'UTC+1 — Paris / Berlin / Rome' },
        { id: 'UTC+2', name: 'UTC+2 — Athens / Helsinki' },
        { id: 'UTC+10', name: 'UTC+10 — Sydney / Melbourne' },
        { id: 'UTC+13', name: 'UTC+13 — Auckland / Wellington' },
    ];

    const currencyChoices = [
        { id: 'GBP', name: 'GBP — British Pound' },
        { id: 'USD', name: 'USD — US Dollar' },
        { id: 'EUR', name: 'EUR — Euro' },
        { id: 'CAD', name: 'CAD — Canadian Dollar' },
        { id: 'AUD', name: 'AUD — Australian Dollar' },
        { id: 'NZD', name: 'NZD — New Zealand Dollar' },
    ];

    const transform = data => {
        ['id', 'name', 'address', 'created_at', 'updated_at']
            .forEach((f) => { delete data[f] });
        return data;
    };

    return (
        <Edit basePath="" resource="settings" id={production.id} title="Settings" transform={transform} {...rest}>
            <SimpleForm redirect="/settings">
                <SectionHeading title="Production Details" />
                <SelectInput source="project_type" choices={projectTypeChoices} allowEmpty />
                <SelectInput source="budget_band" choices={budgetBandChoices} allowEmpty helperText="HETV only" />
                <TextInput source="other_description" fullWidth helperText="Required when project type is Other" />

                <SectionHeading title="Base Address" />
                <BooleanInput source="base_address_same_as_registered" label="Base address same as registered" />
                <TextInput source="base_address_1" label="Base Address Line 1" fullWidth />
                <TextInput source="base_address_2" label="Base Address Line 2" fullWidth />
                <TextInput source="base_address_city" label="Base City" />
                <TextInput source="base_address_county" label="Base County" />
                <TextInput source="base_address_postcode" label="Base Postcode" />
                <TextInput source="base_address_country" label="Base Country" />

                <SectionHeading title="Production Dates" />
                <Box display="flex" style={{ gap: '16px', width: '100%' }}>
                    <DateInput source="pre_prep_start_date" label="Pre-Prep Start" />
                    <DateInput source="pre_prep_end_date" label="Pre-Prep End" />
                </Box>
                <Box display="flex" style={{ gap: '16px', width: '100%' }}>
                    <DateInput source="prep_start_date" label="Prep Start" />
                    <DateInput source="prep_end_date" label="Prep End" />
                </Box>
                <Box display="flex" style={{ gap: '16px', width: '100%' }}>
                    <DateInput source="shoot_start_date" label="Shoot Start" />
                    <DateInput source="shoot_end_date" label="Shoot End" />
                </Box>
                <Box display="flex" style={{ gap: '16px', width: '100%' }}>
                    <DateInput source="wrap_start_date" label="Wrap Start" />
                    <DateInput source="wrap_end_date" label="Wrap End" />
                </Box>

                <HiatusesPanel resource="hiatuses" label="Production Hiatuses" />

                <SectionHeading title="Rates" />
                <SelectInput source="timezone" choices={timezoneChoices} allowEmpty />
                <SelectInput source="currency" choices={currencyChoices} allowEmpty />
                <NumberInput source="mileage_rate" helperText="Per mile rate" />
                <NumberInput source="holiday_pay_percentage" helperText="e.g. 12.07" />
                <NumberInput source="meal_allowance_rate" />

                <SectionHeading title="Per Diems" />
                <SelectInput source="per_diem_type" choices={perDiemTypeChoices} allowEmpty />
                <NumberInput source="per_diem_breakfast_rate" />
                <NumberInput source="per_diem_lunch_rate" />
                <NumberInput source="per_diem_dinner_rate" />
                <NumberInput source="per_diem_single_rate" />
                <NumberInput source="per_diem_non_shoot_rate" />
                <NumberInput source="per_diem_shoot_rate" />

                <SectionHeading title="Timesheet Payment Rules" />
                <BooleanInput
                    source="show_gross_for_daily"
                    label="Show Gross Pay for Daily Contracts"
                    helperText="When on, daily-contract payment summaries show flat gross pay with no separate holiday line"
                />
                <BooleanInput
                    source="ach_holiday_accrual"
                    label="Split ACH Holiday"
                    helperText="When on, Additional Contracted Hour pay splits into net + holiday (subject to the employment type and Show Gross setting above)"
                />
                <BooleanInput
                    source="consecutive_days_reset_weekly"
                    label="Reset Consecutive Day Count Weekly"
                    helperText="When on, the 6th/7th consecutive day count resets at each timesheet week boundary instead of carrying over"
                />

                <SectionHeading title="Timesheets" />
                <SelectInput source="timesheet_pdf_template" choices={timesheetPdfTemplates} optionText="value" optionValue="id" />
                <ReferenceInput label="Current Timesheet Week" source="current_timesheet_week_id" reference="timesheet-weeks" allowEmpty perPage={100}>
                    <FbDateRangeSelectInput optionValue="id" startDateSource="start_date" endDateSource="end_date" extraSource="production_reference" />
                </ReferenceInput>
            </SimpleForm>
        </Edit>
    );
};
